import intl from "react-intl-universal";
import {
  TbAdjustmentsHorizontal,
  TbBuildingBank,
  TbCalendar,
  TbCurrencyReal,
  TbHourglass,
  TbServer,
  TbSwitchHorizontal,
} from "react-icons/tb";
import { ModernFilterBar } from "../../../../../Components/Molecules/Filters/ModernFilterBar";
import {
  AmountFilterPanel,
  CheckboxListFilterPanel,
  DateFilterPanel,
} from "../../../../../Components/Molecules/Filters/ModernFilterPanels";
import {
  EMPTY_BAAS_TRANSACTIONS_FILTERS,
  buildBaasTransactionsFilterTags,
  getBaasTransactionEntityOptions,
  getBaasTransactionInstanceOptions,
  getBaasTransactionStatusOptions,
  getBaasTransactionTypeOptions,
  hasActiveBaasTransactionsFilters,
  isBaasTransactionsChipFilterActive,
  removeBaasTransactionsFilterTag,
} from "../baasTransactionsFilters";

export const BaasTransactionsFilterBar = ({
  filters,
  onFiltersChange,
  filteredItems,
  transactions = [],
  instances = [],
  selectedInstance = null,
  showInstanceFilter = false,
  showPendingFilter = false,
}) => {
  const entitySource = transactions.length ? transactions : filteredItems;
  const entityOptions = getBaasTransactionEntityOptions(entitySource);
  const instanceOptions = getBaasTransactionInstanceOptions(instances, selectedInstance);

  const filterButtons = [
    {
      id: "date",
      icon: TbCalendar,
      label: intl.get("baas.transactions.filterDate"),
      active: isBaasTransactionsChipFilterActive(filters, "date"),
    },
    {
      id: "amount",
      icon: TbCurrencyReal,
      label: intl.get("baas.transactions.filterAmount"),
      active: isBaasTransactionsChipFilterActive(filters, "amount"),
    },
    {
      id: "type",
      icon: TbSwitchHorizontal,
      label: intl.get("baas.transactions.filterType"),
      active: isBaasTransactionsChipFilterActive(filters, "type"),
    },
    {
      id: "status",
      icon: TbAdjustmentsHorizontal,
      label: intl.get("baas.transactions.filterStatus"),
      active: isBaasTransactionsChipFilterActive(filters, "status"),
    },
    ...(showPendingFilter
      ? [
          {
            id: "pending",
            icon: TbHourglass,
            label: intl.get("baas.transactions.filterPending"),
            active: isBaasTransactionsChipFilterActive(filters, "pending"),
          },
        ]
      : []),
    {
      id: "entity",
      icon: TbBuildingBank,
      label: intl.get("baas.transactions.filterEntity"),
      active: isBaasTransactionsChipFilterActive(filters, "entity"),
    },
    ...(showInstanceFilter && instanceOptions.length > 1
      ? [
          {
            id: "instance",
            icon: TbServer,
            label: intl.get("baas.transactions.filterInstance"),
            active: isBaasTransactionsChipFilterActive(filters, "instance"),
          },
        ]
      : []),
  ];

  const renderPanelContent = (panelId, draft, onChange) => {
    switch (panelId) {
      case "date":
        return <DateFilterPanel draft={draft} onChange={onChange} />;
      case "amount":
        return (
          <AmountFilterPanel
            draft={draft}
            onChange={onChange}
            idPrefix="baas-transactions-amount"
          />
        );
      case "type":
        return (
          <CheckboxListFilterPanel
            options={getBaasTransactionTypeOptions()}
            selectedValues={draft.types}
            onChange={(types) => onChange({ ...draft, types })}
            idPrefix="baas-transactions-type"
          />
        );
      case "status":
        return (
          <CheckboxListFilterPanel
            options={getBaasTransactionStatusOptions()}
            selectedValues={draft.statuses}
            onChange={(statuses) => onChange({ ...draft, statuses })}
            idPrefix="baas-transactions-status"
          />
        );
      case "pending":
        return (
          <CheckboxListFilterPanel
            options={[
              {
                id: "pendingPixOnly",
                label: intl.get("baas.transactions.filterPendingHint"),
              },
            ]}
            selectedValues={draft.pendingPixOnly ? ["pendingPixOnly"] : []}
            onChange={(selected) =>
              onChange({
                ...draft,
                pendingPixOnly: selected.includes("pendingPixOnly"),
              })
            }
            idPrefix="baas-transactions-pending"
          />
        );
      case "entity":
        return (
          <CheckboxListFilterPanel
            options={entityOptions}
            selectedValues={draft.entityIds}
            onChange={(entityIds) => onChange({ ...draft, entityIds })}
            idPrefix="baas-transactions-entity"
            searchable
            searchPlaceholder={intl.get("baas.transactions.filterEntitySearch")}
          />
        );
      case "instance":
        return (
          <CheckboxListFilterPanel
            options={instanceOptions}
            selectedValues={draft.instanceIds || []}
            onChange={(instanceIds) => onChange({ ...draft, instanceIds })}
            idPrefix="baas-transactions-instance"
            searchable
            searchPlaceholder={intl.get("baas.transactions.filterInstanceSearch")}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ModernFilterBar
      filters={filters}
      onFiltersChange={onFiltersChange}
      emptyFilters={EMPTY_BAAS_TRANSACTIONS_FILTERS}
      searchPlaceholder={intl.get("baas.transactions.searchPlaceholder")}
      filterButtons={filterButtons}
      buildFilterTags={(currentFilters) =>
        buildBaasTransactionsFilterTags(currentFilters, entitySource, instanceOptions)
      }
      hasActiveFilters={hasActiveBaasTransactionsFilters}
      removeFilterTag={removeBaasTransactionsFilterTag}
      renderPanelContent={renderPanelContent}
      filteredItems={filteredItems}
      panelTitles={{
        date: intl.get("baas.transactions.filterDate"),
        amount: intl.get("baas.transactions.filterAmount"),
        type: intl.get("baas.transactions.filterType"),
        status: intl.get("baas.transactions.filterStatus"),
        pending: intl.get("baas.transactions.filterPending"),
        entity: intl.get("baas.transactions.filterEntity"),
        instance: intl.get("baas.transactions.filterInstance"),
      }}
    />
  );
};
